import {
  Button,
  colors,
  IconButton,
  LabeledCheckbox,
  LabeledIconInput,
  LabeledRadio,
  SearchIcon,
} from "@commonsku/styles";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import { getColors, getSizes } from "../../shop/helpers";
import { getFullItem, getColorsAndSizesForNonPSItem } from "../../../selectors";
import { createAddVariant } from "../../../actions/item";
import { ItemVariant } from "../../../interfaces/ItemVariant";
import NewVariantInput from "./NewVariantInput";
import { createSelectSkuOptionsPopup } from "../../../actions/popup";
import ReactTooltip from "../../helpers/ReactTooltip";
import { rebuildTooltip } from "../../helpers";

const isVariantAlreadyAdded = (
  itemVariants: ItemVariant[],
  color_id: string | null,
  size_id: string | null
) => {
  return itemVariants.some((variant) => {
    let skuOptions: { color?: string; size?: string } = {};

    if (variant.product_sku_id && variant.product_sku?.sku_options) {
      try {
        skuOptions = JSON.parse(variant.product_sku.sku_options);
      } catch (e) {
        console.log(
          "Failed parsing the SKU Options",
          variant.product_sku.sku_options,
          e
        );
      }
    }

    const variantColor = skuOptions.color ?? variant.color_id ?? null;
    const variantSize = skuOptions.size ?? variant.size_id ?? null;

    if (color_id && size_id) {
      return variantColor === color_id && variantSize === size_id;
    } else if (color_id) {
      return variantColor === color_id;
    } else if (size_id) {
      return variantSize === size_id;
    }

    return false;
  });
};

const TOOLTIP_CONFIG = {
  id: "size-and-color-tooltip",
  place: "bottom" as const,
  delayShow: 300,
};

const labelStyle = {
  width: 220,
  height: 36,
  alignItems: "center",
  paddingLeft: 0,
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const radioLabelStyle = (isSelected: boolean) => ({
  color: colors.primary1["70"],
  border: isSelected
    ? `3px solid ${colors.primary1["70"]}`
    : `3px solid ${colors.primary1["20"]}`,
  background: isSelected ? colors.white : colors.primary1["20"],
  borderRadius: 200,
  width: 220,
  height: 40,
  fontSize: 14,
  fontWeight: 500,
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  paddingTop: 5,
});

const columnHeaderStyle = {
  marginBottom: 8,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const WrapperDiv = styled.div`
  margin-left: 10px !important;
  background: #ffffff;
  width: 551px;
  height: 361px;
  border: 2px solid ${colors.primary1["65"]};
  border-radius: 5px;
  padding: 16px !important;
`;

const ColumnWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin-top: 8px !important;
`;

const Column = styled.div`
  width: 240px;
  height: 250px;
  overflow-y: hidden;
`;

const ColumnHeader = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${colors.neutrals["90"]};
`;

type SizeAndColorDropdownProps = {
  item_id: string;
  onClose: () => void;
};

type ColorType = {
  color_id: string;
  color_name: string;
};

type SizeType = {
  size_id: string;
  size_name: string;
};

const SizeAndColorDropdown = ({
  item_id,
  onClose,
}: SizeAndColorDropdownProps) => {
  const [selectedColors, setSelectedColors] = useState<ColorType[]>([]);
  const [selectedSizes, setSelectedSizes] = useState<SizeType[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const dispatch = useDispatch();
  const item = useSelector((state) => getFullItem(state, { item_id }));
  const skus = useMemo(() => item?.item_skus || [], [item]);
  const isPsProduct = skus.length > 0;

  const colorAndSizes = useSelector((state) =>
    getColorsAndSizesForNonPSItem(state, { item_id })
  );

  const productColors: ColorType[] = useMemo(
    () =>
      isPsProduct
        ? getColors(isPsProduct, item, skus)
        : colorAndSizes.colors || [],
    [isPsProduct, item, skus, colorAndSizes.colors]
  );
  const hasColors = productColors.length > 0;

  const sizes: SizeType[] = useMemo(
    () =>
      isPsProduct
        ? getSizes(
            selectedColors.length > 0
              ? selectedColors.map((color) => ({
                  option_axis: "color",
                  option_name: color.color_id,
                }))
              : [],
            isPsProduct,
            item,
            skus
          )
        : colorAndSizes.sizes || [],
    [isPsProduct, selectedColors, item, skus, colorAndSizes.sizes]
  );
  const hasSizes = sizes.length > 0;

  const filteredColors = useMemo(
    () =>
      productColors.filter((color) =>
        color.color_name.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [productColors, searchTerm]
  );

  const filteredSizes = useMemo(
    () =>
      !hasColors
        ? sizes.filter((size) =>
            size.size_name.toLowerCase().includes(searchTerm.toLowerCase())
          )
        : sizes,
    [hasColors, sizes, searchTerm]
  );

  const isVariantAdded = useCallback(
    (color_id: string | null, size_id: string | null) => {
      const itemVariants = item?.variants || [];
      return isVariantAlreadyAdded(itemVariants, color_id, size_id);
    },
    [item?.variants]
  );

  const handleColorSelect = (color: ColorType) => {
    if (hasSizes) {
      setSelectedColors([color]);
      setSelectedSizes([]);
    } else {
      setSelectedColors((prev) =>
        prev.some((c) => c.color_id === color.color_id)
          ? prev.filter((c) => c.color_id !== color.color_id)
          : [...prev, color]
      );
    }
  };

  const handleSizeSelect = (size: SizeType) => {
    setSelectedSizes((prev) =>
      prev.some((s) => s.size_id === size.size_id)
        ? prev.filter((s) => s.size_id !== size.size_id)
        : [...prev, size]
    );
  };

  const handleSelectAll = (type: "color" | "size") => {
    if (type === "color") {
      if (selectedColors.length === 0) {
        const newColors = filteredColors.filter(
          (color) => !isVariantAdded(color.color_id, null)
        );
        setSelectedColors(newColors);
      } else {
        setSelectedColors([]);
      }
    } else if (type === "size") {
      if (selectedSizes.length === 0) {
        const newSizes = filteredSizes.filter((size) => {
          if (hasColors) {
            const selectedColor = selectedColors[0];
            if (!selectedColor) return false;
            return !isVariantAdded(selectedColor.color_id, size.size_id);
          } else {
            return !isVariantAdded(null, size.size_id);
          }
        });
        setSelectedSizes(newSizes);
      } else {
        setSelectedSizes([]);
      }
    }
  };

  useEffect(() => {
    rebuildTooltip();
  }, [selectedColors, selectedSizes]);

  const handleDone = () => {
    if (
      (hasColors && selectedColors.length === 0) ||
      (hasSizes && selectedSizes.length === 0)
    ) {
      onClose();
      return;
    }

    if (hasColors && hasSizes) {
      selectedColors.forEach((color) => {
        selectedSizes.forEach((size) => {
          const sku = skus.find(
            (sku) =>
              sku.options.some(
                (opt) =>
                  opt.option_axis === "color" &&
                  opt.option_name === color.color_id
              ) &&
              sku.options.some(
                (opt) =>
                  opt.option_axis === "size" && opt.option_name === size.size_id
              )
          );

          dispatch(
            createAddVariant(item_id, {
              color_id: color.color_id || "TBD",
              size_id: size.size_id || "TBD",
              product_sku_id: sku?.product_sku_id,
            })
          );
        });
      });
    } else if (hasColors) {
      selectedColors.forEach((color) => {
        const sku = skus.find((sku) =>
          sku.options.some(
            (opt) =>
              opt.option_axis === "color" && opt.option_name === color.color_id
          )
        );

        dispatch(
          createAddVariant(item_id, {
            color_id: color.color_id,
            size_id: "TBD",
            product_sku_id: sku?.product_sku_id,
          })
        );
      });
    } else if (hasSizes) {
      selectedSizes.forEach((size) => {
        const sku = skus.find((sku) =>
          sku.options.some(
            (opt) =>
              opt.option_axis === "size" && opt.option_name === size.size_id
          )
        );

        dispatch(
          createAddVariant(item_id, {
            color_id: "TBD",
            size_id: size.size_id,
            product_sku_id: sku?.product_sku_id,
          })
        );
      });
    }

    onClose();
  };

  const searchPlaceholder = hasColors
    ? "Find color"
    : !hasSizes
    ? "Add options"
    : "Find size";

  return (
    <WrapperDiv>
      <ReactTooltip {...TOOLTIP_CONFIG} />
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <LabeledIconInput
          Icon={<SearchIcon />}
          iconLabelStyles={{ background: "white" }}
          iconColor="#B8C4CB"
          style={{ width: 240 }}
          placeholder={searchPlaceholder}
          containerStyle={{ border: "none" }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={!hasColors && !hasSizes}
        />
        {isPsProduct && (
          <IconButton
            onClick={() =>
              dispatch(
                createSelectSkuOptionsPopup(item_id, item?.parent_id, true)
              )
            }
            variant="text"
            style={{
              marginBottom: 0,
              textAlign: "right",
            }}
          >
            Update Options
          </IconButton>
        )}
      </div>

      <ColumnWrapper>
        {hasColors ? (
          <Column>
            <div style={columnHeaderStyle}>
              <ColumnHeader>Color</ColumnHeader>
              {!hasSizes && (
                <Button
                  variant="text"
                  size="tiny"
                  style={{
                    marginBottom: 0,
                  }}
                  onClick={() => handleSelectAll("color")}
                >
                  {selectedColors.length > 0 ? "Unselect All" : "Select All"}
                </Button>
              )}
            </div>
            <div
              style={{
                height: "85%",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              {filteredColors.map((color) =>
                hasSizes ? (
                  <div
                    key={color.color_id}
                    data-for="size-and-color-tooltip"
                    data-tip={color.color_name}
                  >
                    <LabeledRadio
                      label={color.color_name}
                      checked={selectedColors.some(
                        (c) => c.color_id === color.color_id
                      )}
                      onClick={() => handleColorSelect(color)}
                      labelStyle={
                        radioLabelStyle(
                          selectedColors.some(
                            (c) => c.color_id === color.color_id
                          )
                        ) as React.CSSProperties
                      }
                      style={{
                        display: "none",
                      }}
                    />
                  </div>
                ) : (
                  <div
                    key={color.color_id}
                    data-for="size-and-color-tooltip"
                    data-tip={color.color_name}
                  >
                    <LabeledCheckbox
                      label={color.color_name}
                      checked={
                        selectedColors.some(
                          (c) => c.color_id === color.color_id
                        ) || isVariantAdded(color.color_id, null)
                      }
                      onClick={() => {
                        if (isVariantAdded(color.color_id, null)) {
                          return;
                        }
                        handleColorSelect(color);
                      }}
                      labelStyle={labelStyle as React.CSSProperties}
                      disabled={isVariantAdded(color.color_id, null)}
                      style={{
                        display: "none",
                      }}
                      checkboxStyle={{ position: "relative", top: 6 }}
                    />
                  </div>
                )
              )}
              {!isPsProduct && (
                <NewVariantInput inputType="COLOR" item_id={item_id} />
              )}
            </div>
          </Column>
        ) : (
          <Column>
            <div style={columnHeaderStyle}>
              <ColumnHeader>Color</ColumnHeader>
            </div>
            <div
              style={{ height: "85%", overflowY: "auto", overflowX: "hidden" }}
            >
              {!isPsProduct && (
                <NewVariantInput inputType="COLOR" item_id={item_id} />
              )}
            </div>
          </Column>
        )}

        {hasSizes && (!hasColors || selectedColors.length > 0) && (
          <Column>
            <div style={columnHeaderStyle}>
              <ColumnHeader>Size</ColumnHeader>
              <Button
                variant="text"
                size="tiny"
                style={{
                  marginBottom: 0,
                }}
                onClick={() => handleSelectAll("size")}
              >
                {selectedSizes.length > 0 ? "Unselect All" : "Select All"}
              </Button>
            </div>
            <div
              style={{
                height: "85%",
                width: "100%",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              {filteredSizes.map((size) => (
                <div
                  key={size.size_id}
                  data-for="size-and-color-tooltip"
                  data-tip={size.size_name}
                >
                  <LabeledCheckbox
                    label={size.size_name}
                    checked={
                      selectedSizes.some((s) => s.size_id === size.size_id) ||
                      isVariantAdded(
                        hasColors ? selectedColors[0]?.color_id : null,
                        size.size_id
                      )
                    }
                    onClick={() => {
                      if (
                        isVariantAdded(
                          hasColors ? selectedColors[0]?.color_id : null,
                          size.size_id
                        )
                      ) {
                        return;
                      }
                      handleSizeSelect(size);
                    }}
                    labelStyle={labelStyle as React.CSSProperties}
                    disabled={isVariantAdded(
                      hasColors ? selectedColors[0]?.color_id : null,
                      size.size_id
                    )}
                    style={{ display: "none" }}
                    checkboxStyle={{ position: "relative", top: 6 }}
                  />
                </div>
              ))}
              {!isPsProduct && (
                <NewVariantInput inputType="SIZE" item_id={item_id} />
              )}
            </div>
          </Column>
        )}
        {!hasSizes && selectedColors.length > 0 && !isPsProduct && (
          <Column>
            <div style={columnHeaderStyle}>
              <ColumnHeader>Size</ColumnHeader>
            </div>
            <div
              style={{
                height: "85%",
                width: "100%",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              <NewVariantInput inputType="SIZE" item_id={item_id} />
            </div>
          </Column>
        )}
      </ColumnWrapper>

      <div style={{ textAlign: "right" }}>
        <Button onClick={handleDone} size="medium">
          Done
        </Button>
      </div>
    </WrapperDiv>
  );
};

export default SizeAndColorDropdown;
